
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { uiStore, configStore, documentStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import defensiveCopy from "@/util/defensive-copy";
import StructureContextMenu from "@/components/Structure/StructureContextMenu.vue";
import Icon from "@/components/UI/Icon.vue";
import ElementIcon from "@/components/UI/ElementIcon.vue";
import { openUiPanel, whichSideToPutUiPanel } from "@/lib/panels/panels";
import { structurePanelNodeIsExpanded } from "@/lib/structure-panel/expand-collapse";
import { SECTION_SLUG } from "@/lib/elements/constants";
import lodash from "lodash";
import { forceNextTick } from "@/util/force-next-tick";

const nodeIndentAmount = 16;

@Component({
  name: "StructureNode",
  components: {
    ElementIcon,
    Icon,
    StructureContextMenu
  }
})
export default class StructureNode extends Vue {
  @Prop()
  element!: TreeNode;

  @Prop()
  depth: any;

  showElementNameFallback = true;

  editingFriendlyName = false;

  get shouldShowChildrenNodes() {
    return structurePanelNodeIsExpanded(this.element.id);
  }

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ChildStructureNodes =
      require("./ChildStructureNodes.vue").default;
  }

  activateElement() {
    uiStore.activateElement(this.element.id);

    if (
      whichSideToPutUiPanel("elementproperties") !==
      whichSideToPutUiPanel("structure")
    ) {
      openUiPanel("elementproperties");
    }
  }

  get baseElement() {
    return configStore.element(this.element.data.type);
  }

  get activeClass() {
    return {
      "breakdance--active-element": uiStore.activeElementId === this.element.id
    };
  }

  get indentStyle() {
    const indentAmt = this.depth * nodeIndentAmount;
    return {
      marginLeft: `${indentAmt}px`,
      width: `calc(100% - 1px - ${indentAmt}px`
    };
  }

  get nestDeeperStyle() {
    const indentAmt = (this.depth + 1) * nodeIndentAmount;
    return {
      left: `${indentAmt}px`
    };
  }

  get nestShallowerStyle() {
    const indentAmt = this.depth * nodeIndentAmount;
    return {
      width: `${indentAmt}px`
    };
  }

  get elementName() {
    return configStore.element(this.element.data.type).name;
  }

  get currentlyDragging() {
    return uiStore.currentlyDraggingCanvasElementId === this.element.id;
  }

  get isShowingIdsInStructurePanel() {
    return uiStore.settings.showElementIdInStructurePanel;
  }

  duplicate() {
    documentStore.duplicateElementAndActivateIt(this.element.id);
  }

  showContextMenu(e: MouseEvent) {
    uiStore.setOnStructurePanelRightClickMenuState({
      elementId: this.element.id,
      position: {
        x: e.clientX,
        y: e.clientY
      }
    });
  }

  remove() {
    documentStore.deleteElement({ nodeId: this.element.id });
  }

  toggleChildren() {
    const newStructurePanelNodesExpandedState = defensiveCopy(
      uiStore.structurePanelNodesExpandedState
    );

    newStructurePanelNodesExpandedState[this.element.id] =
      !this.shouldShowChildrenNodes;

    uiStore.setStructurePanelNodesExpandedState(
      newStructurePanelNodesExpandedState
    );
  }

  get isDraftSection() {
    if (this.element.data.type === SECTION_SLUG) {
      return lodash.get(
        this.element.data.properties,
        "settings.advanced.draft"
      );
    }

    return false;
  }

  get friendlyNameInputValue() {
    if (
      this.element.data.properties?.meta?.friendlyName === undefined &&
      // If the user deletes the whole input, don't go back to showing it
      this.showElementNameFallback
    ) {
      this.showElementNameFallback = false;

      return this.elementName;
    }

    // need this to trick the computed prop into re-rendering. Otherwise, it won't rerun and update
    if (this.editingFriendlyName) {
      // if it already has a name, don't show the fallback when deleting it
      this.showElementNameFallback = false;
    }

    return (
      this.element.data.properties?.meta?.friendlyName &&
      this.element.data.properties?.meta?.friendlyName.trim()
    );
  }

  enableEditingFriendlyName() {
    this.editingFriendlyName = true;

    // wait for it to show
    forceNextTick(() => {
      // @ts-ignore
      this.$refs.friendlyNameInputRef.focus();
      // @ts-ignore
      this.$refs.friendlyNameInputRef.select();
    });
  }

  updateFriendlyName(friendlyName: string | undefined) {
    documentStore.unthrottledPropertyChanged({
      elementId: this.element.id,
      path: "meta.friendlyName",
      meta: {
        snapshotLabel: `Updated friendly name for element "${this.elementName}"`
      },
      value: friendlyName?.trim() || undefined
    });
  }

  disableEditing() {
    this.editingFriendlyName = false;
    this.showElementNameFallback = true;
  }

  // don't drag or open context menu
  noop(e: Event) {
    e.stopPropagation();
    return {};
  }
}
